import React, { useState, useCallback, useEffect, useRef, useContext, Suspense } from 'react';
import { Persona } from '@fluentui/react/lib/Persona';
import { StoreState } from 'src/Store';
import { appInsights } from '../Services/Insights';
import {
  ResizeGroup,
  TooltipHost,
  PersonaSize,
  IPanel,
  TeachingBubble,
  DirectionalHint,
  Link,
  IContextualMenuItemProps,
  Stack,
  Panel,
  Toggle,
} from '@fluentui/react';
import { UserContext } from '../Models/UserProfile';
import { GetUserAppInsightProperties } from 'src/utils/AppInsightProperties';
import { HttpVerbs, TrueFalse, UATConstants } from 'src/Common/Models/UATConstants';
import { updateGlobalSearchInput } from 'src/App/Reducers/FilterReducer';
import { useHistory } from 'react-router-dom';
import { ServiceFactoryClientExecuteApi } from 'src/Common/Services/ServiceFactoryToExecuteApi';
import { AnalyticsViewPanel } from 'src/AnalyticsView/AVPanel';
import { getURLParameter } from 'src/utils/utils';
import CopilotIcon from 'src/copilot-v2/Asset/svgs/Copilot.svg?react';
import PeopleAddFluentBase from 'src/icons/PeopleAddFluent.svg?react';
import FluentAnalyticsBase from 'src/icons/FluentAnalytics.svg?react';
import { useDispatch, useSelector } from 'react-redux';

const PeopleAddFluent = (props: React.SVGProps<SVGSVGElement>) => (
  <PeopleAddFluentBase width={21} height={21} {...props} />
);
const FluentAnalytics = (props: React.SVGProps<SVGSVGElement>) => (
  <FluentAnalyticsBase width={21} height={21}  {...props} />
);
import UATlogo from './images/logo.png';
import { isEqual } from 'lodash-es';
// Coherence Design System
import {
  Header,
  HeaderButton,
  usePanels,
  useResizeGroup,
  INotificationBadgeProps,
  NotificationBadge,
} from '@coherence-design-system/header';
import { ProfileCenter } from '@coherence-design-system/profile-center';
import { Template } from '@coherence-design-system/slot';
import { EnhancedSearch } from '@coherence-design-system/enhanced-search';
import { mergeStyles, registerIcons } from '@fluentui/react/lib/Styling';
import { NotificationPanel } from 'src/notifications/components/molecules/NotificationPanel';
import {
  INotificationCategory,
  INotificationItem,
  NotificationStatus,
} from '@coherence-design-system/notification-center';
import { NotificationsContext } from 'src/notifications';
import LanguageDropdown from 'src/LanguageDropdown';
import { getLegacyRecomputeFlag, getServerDrivenFormsFlag, setLegacyRecomputeFlag, setServerDrivenFormsFlag } from 'src/Common/featureFlags';
import { NavigationHamburger } from '@coherence-design-system/navigation';
import { HelpPanel } from 'src/HelpPanel';
import { getTenantId } from 'src/Common/Models/StoreConstants';
import { LocalTenantConfig } from 'src/tenantConfig';
import { ITenantConfig, TenantNames } from 'src/tenantConfig/interface';
import { setNavCollapsed } from 'src/actions/IsNavCollapsed';
import { useIsMobile, Viewport } from '@coherence-design-system/controls';
import { getROBTemetetry } from 'src/utils/helpers/ROBTelemetry';

registerIcons({
  icons: {
    copilotbeta: <CopilotIcon />
  },
});

const copilotBetaClass = mergeStyles({
  svg: {
    position: 'relative',
    top: '-8px',
    '@media(max-width: 650px)': {
      top: '2px',
      left: '-5px',
      fill: 'black',
      path: {
        fill: 'black',
      },
    },
  },
});


export interface NavbarProps {
  user?: UserContext;
  profileBase64: string;
  account: any;
  onHamburgerClick: (data: boolean) => void;
  isNavCollapsed: boolean;
  toggleNavCollapsed: () => void;
}

const NavBar: React.FC<NavbarProps> = (props) => {

  // States
  const { legacyNotifications } = useContext(NotificationsContext);
  const [notificationsCount, setCountNotifications] = useState<number>(0);
  const [serverDrivenForms, setServerDrivenForms] = useState<boolean>(getServerDrivenFormsFlag());
  const [legacyRecompute, setLegacyRecompute] = useState<boolean>(getLegacyRecomputeFlag());
  const notificationRef = useRef<IPanel>(null);
  const settingsPanelRef = useRef<IPanel>(null);
  const helpPanelRef = useRef<IPanel>(null);

  // History hook and Redux state
  const historyHook = useHistory();
  const isMobile = useIsMobile(Viewport.MEDIUM);
  const urlParams = new window.URL(window.location.href).searchParams;
  const urlTenantID = urlParams.get('tenantId')?.toString() || '';
  const tenantConfig: ITenantConfig = LocalTenantConfig[urlTenantID] as ITenantConfig;
  const currentTenantData = tenantConfig || {};

  // Feature flags with proper TypeScript defaults
  const isGlobalSearchEnabled = currentTenantData?.FeatureConfig?.Navigation?.GlobalSearch ?? true;
  const isCopilotEnabled = currentTenantData?.FeatureConfig?.Navigation?.Copilot ?? true;
  const isAnalyticsEnabled = currentTenantData?.FeatureConfig?.Navigation?.Analytics ?? true;
  const isRequestAccessEnabled = currentTenantData?.FeatureConfig?.Navigation?.RequestAccess ?? true;
  const isSettingsEnabled = currentTenantData?.FeatureConfig?.Navigation?.Settings ?? true;
  const isHelpEnabled = currentTenantData?.FeatureConfig?.Navigation?.Help ?? true;
  const isNotificationsEnabled = currentTenantData?.FeatureConfig?.Navigation?.Notifications ?? true;

  const dispatch = useDispatch();

  // Enhanced registerEventForAppInsight function with proper TypeScript typing
  const registerEventForAppInsight = useCallback((
    name: string,
    properties?: Record<string, any>
  ): void => {
    try {
      appInsights.trackEvent(
        {
          name,
          properties: {
            ...properties,
            component: 'NavBar',
            version: '1.0.0',
          },
        },
        GetUserAppInsightProperties()
      );
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }, []);

  // Coherence Design System Functions First (needed for handleSettingsClick)
  const profileCenterRef = useRef<IPanel>(null);
  const { currentPanel, openPanel, closePanel } = usePanels();
  const { onReduceData, onGrowData, onRenderData } = useResizeGroup({
    closePanel,
    currentPanel,
  });

  // Settings Panel Event Handler
  const handleSettingsClick = useCallback((): void => {
    openPanel(settingsPanelRef);
    registerEventForAppInsight('Settings Panel Opened', {
      timestamp: new Date().toISOString(),
      tenantId: urlTenantID,
    });
  }, [urlTenantID, openPanel, settingsPanelRef, registerEventForAppInsight]);


  // Functions & Methods Start
  const copilotWindowOpenAction = (): void => {
    setNotificationIconClicked(false);
    if (isMobile) {
      sessionStorage.setItem(UATConstants.copilotPinnedState, 'false');
    } else {
      sessionStorage.setItem(UATConstants.copilotPinnedState, 'true');
    }
    sessionStorage.setItem(UATConstants.showCopilotWindow, 'true');
    window.dispatchEvent(new Event('storage'));
  };

  const openNotifications = (): void => {
    sessionStorage.setItem('showNotification', 'true');
    window.dispatchEvent(new Event('storage'));
  };

  const onSearchInputChanged = (newval: string): void => {
    dispatch(updateGlobalSearchInput(newval));
    setredirectToDashboard(true);
    if (newval === '' && window.location.pathname === UATConstants.Routes.TenantOverviewHub) {
      const globalSearch = document.getElementById("searchbox") as HTMLInputElement;
      if (globalSearch) {
        globalSearch.value = '';
      }
    }
    if (urlTenantID && newval != '' && (window.location.pathname === UATConstants.Routes.ManageAction || window.location.pathname === UATConstants.Routes.ManageActionPage)) {
      historyHook.push(UATConstants.Routes.TenantOverviewHub + '?tenantId=' + urlTenantID + '&gsearch=' + newval);
    }
    else if (!urlTenantID && newval != '' && (window.location.pathname === UATConstants.Routes.ManageAction || window.location.pathname === UATConstants.Routes.ManageActionPage)) {
      historyHook.push(UATConstants.Routes.Overview + '?gsearch=' + newval);
    }
  };

  const openAVPanel = (newval: boolean): void => {
    setshowAVPanel(newval);
  };

  const renderAVPanel = (): JSX.Element => {
    return (
      <Suspense fallback={<></>}>
        <AnalyticsViewPanel
          isPanelVisible={showAVPanel}
          onPanelDismiss={() => {
            openAVPanel(false);
          }}
          panelStyles={{
            main: {
              backgroundColor: '#f3f2f1',
              height: '100%',
              top: 39,
            },
            commands: { backgroundColor: '#f3f2f1' },
          }}
          panelProps={{
            onDismiss: () => {
              openAVPanel(false);
            },
            isOpen: showAVPanel === true,
          }}
        />
      </Suspense>
    );
  };

  const renderRedirectComponent = (): void | boolean => {
    setredirectToDashboard(false);

    const homePageRoutes = [
      UATConstants.Routes.Overview,
      UATConstants.Routes.Index,
      UATConstants.Routes.IndexHtml,
      UATConstants.Routes.TenantOverviewHub,
    ];

    const index = homePageRoutes.findIndex((route) => {
      return route.toLowerCase() === window.location.pathname.toLowerCase();
    });

    if (index >= 0) {
      return true;
    } else {
      return historyHook.push(UATConstants.Routes.Overview);
    }
  };

  const toggleTeachingBubbleVisible = (dismiss: boolean, category: string): void => {
    const notification = legacyNotifications.filter(
      (notification: any) => notification.category === category
    )[0];
    const notificationId = notification?.id;

    const ids: string[] = [notificationId];
    if (dismiss) {
      const userID = sessionStorage.getItem(UATConstants.jwtUserAccountEmail);
      if (userID !== null && userID !== undefined) {
        const userNotificationDetails = {
          userID: userID,
          notificationIDs: ids,
        };
        ServiceFactoryClientExecuteApi.ExecuteRequest<any>(
          UATConstants.ApiPaths.PostDisableUserLevelNotifications,
          [],
          HttpVerbs.post,
          userNotificationDetails,
          (response: boolean) => {
            if (category === UATConstants.NotificationCategory.FeatureCopilot) {
              setshowTeachingBubbleForCopilot(false);
            } else if (category === UATConstants.NotificationCategory.FeatureCopilotMoreIcon) {
              setshowTeachingBubbleForCopilot(false);
            }
          },
          (response: any) => null,
          false
        );

        appInsights.trackEvent(
          {
            name:
              category === UATConstants.NotificationCategory.FeatureCopilot
                ? 'UAT-Copilot-NotificationBubble-Dismissed'
                : 'UAT-Copilot-NotificationBubbleMoreIcon-Dismissed',
            properties: {
              notificationID: notificationId,
              name: category,
            },
          },
          GetUserAppInsightProperties()
        );
      }
    } else {
      if (category === UATConstants.NotificationCategory.FeatureCopilot) {
        setshowTeachingBubbleForCopilot(false);
      } else if (category === UATConstants.NotificationCategory.FeatureCopilotMoreIcon) {
        setshowTeachingBubbleForCopilot(false);
      }
      appInsights.trackEvent(
        {
          name:
            category === UATConstants.NotificationCategory.FeatureCopilot
              ? 'UAT-Copilot-NotificationBubble-Closed'
              : 'UAT-Copilot-NotificationBubbleMoreIcon-Closed',
          properties: {
            notificationID: notificationId,
            name: category,
          },
        },
        GetUserAppInsightProperties()
      );
    }
  };
  //Functions & Methods End

  //Redux store start
  const globalSearchFilterTextRedux: any = useSelector<StoreState>(
    (state) => state.filter.globalSearchFilterText
  );
  //Redux store end

  //Component state start
  const tenantUrlValue = getURLParameter('tenantId');
  const [profileBase64, setprofileBase64] = useState<string>('');
  const [redirectToDashboard, setredirectToDashboard] = useState<boolean>(false);
  const [showTeachingBubbleForCopilot, setshowTeachingBubbleForCopilot] = useState<boolean>(
    legacyNotifications &&
    legacyNotifications.some(
      (a: any) => a.category === UATConstants.NotificationCategory.FeatureCopilot
    )
  );
  const [showTeachingBubbleForCopilotMoreIcon, setshowTeachingBubbleForCopilotMoreIcon] =
    useState<boolean>(
      legacyNotifications &&
      legacyNotifications.some(
        (a: any) => a.category === UATConstants.NotificationCategory.FeatureCopilotMoreIcon
      )
    );

  const [showAVPanel, setshowAVPanel] = useState<boolean>(false);

  const [globalSearchFilterText, setglobalSearchFilterText] = useState<string>(
    globalSearchFilterTextRedux ? globalSearchFilterTextRedux : ''
  );
  const [previousPath, setPreviousPath] = useState<string>('');

  useEffect(() => {
    setglobalSearchFilterText(globalSearchFilterTextRedux);
    if (window.location.pathname.toLowerCase() !== previousPath) {
      setPreviousPath(window.location.pathname.toLowerCase());
    }
  }, [globalSearchFilterTextRedux, window.location.pathname]);

  useEffect(() => {
    dispatch(setNavCollapsed(props.isNavCollapsed));
  }, [props.isNavCollapsed, dispatch]);


  //Component state end
  //Class component render function code start
  const currentURL = window.location.href;
  let isParam = false;

  if (
    currentURL.toLowerCase().indexOf('createaction/iat') > -1 ||
    currentURL.toLowerCase().indexOf('createaction/field') > -1 ||
    currentURL.toLowerCase().indexOf('createaction/corp') > -1 ||
    currentURL.toLowerCase().indexOf('createaction/supportdelivery') > -1 ||
    currentURL.toLowerCase().indexOf('createaction/supportdeliveryscrum') > -1
  ) {
    isParam = true;
  }

  let isCopilotDisabled = false;

  if (currentURL.indexOf('manageview') > -1) {
    isCopilotDisabled = true;
  }

  const buttonId = 'copilotIcon';

  const homeHref: string = constructHomeHref();

  function constructHomeHref(): string {
    const cPanel =
      sessionStorage.getItem(UATConstants.showCopilotWindow) !== null
        ? sessionStorage.getItem(UATConstants.showCopilotWindow) === TrueFalse.false
          ? false
          : true
        : getURLParameter('cw')?.toString() === TrueFalse.false
          ? false
          : true;
    return window.location.origin + '/?cw=' + cPanel;
  }
  //Class component render function code end

  //Component lifecycle start

  //componentDidMount Start
  useEffect(() => {
    if (showTeachingBubbleForCopilot) {
      const notification = legacyNotifications.filter(
        (notification: any) =>
          notification.category === UATConstants.NotificationCategory.FeatureCopilot
      )[0];
      const notificationId = notification?.id;

      appInsights.trackEvent(
        {
          name: 'UAT-Copilot-NotificationBubble-Shown',
          properties: {
            notificationID: notificationId,
            name: UATConstants.NotificationCategory.FeatureCopilot,
          },
        },
        GetUserAppInsightProperties()
      );
    }
    if (showTeachingBubbleForCopilotMoreIcon) {
      const notification = legacyNotifications.filter(
        (notification: any) =>
          notification.category === UATConstants.NotificationCategory.FeatureCopilotMoreIcon
      )[0];
      const notificationId = notification?.id;

      appInsights.trackEvent(
        {
          name: 'UAT-Copilot-MoreIcon-NotificationBubble-Shown',
          properties: {
            notificationID: notificationId,
            name: UATConstants.NotificationCategory.FeatureCopilotMoreIcon,
          },
        },
        GetUserAppInsightProperties()
      );
    }
  }, [showTeachingBubbleForCopilot, showTeachingBubbleForCopilotMoreIcon, legacyNotifications]);
  //componentDidMount End

  //reset GlobalSearch on tenant change
  useEffect(() => {
    dispatch(updateGlobalSearchInput(''));
  }, [urlTenantID, dispatch]);

  //componentDidUpdate Start
  useEffect(() => {
    if (legacyNotifications) {
      setshowTeachingBubbleForCopilot(
        legacyNotifications &&
        legacyNotifications.some(
          (a: any) => a.category === UATConstants.NotificationCategory.FeatureCopilot
        )
      );
      setshowTeachingBubbleForCopilotMoreIcon(
        legacyNotifications &&
        legacyNotifications.some(
          (a: any) => a.category === UATConstants.NotificationCategory.FeatureCopilotMoreIcon
        )
      );
    }
  }, [legacyNotifications]);
  // componentDidUpdate End

  //componentWillReceiveProps Start
  useEffect(() => {
    if (props.profileBase64) {
      setprofileBase64(props.profileBase64);
    }
  }, [props.profileBase64]);
  //componentWillReceiveProps End
  //Component lifecycle end

  // Profile Center
  const profileProps = {
    persona: {
      imageUrl: profileBase64,
      imageAlt: sessionStorage.getItem('jwtUserAccountName') || '',
      text: props.user?.displayName || '',
      secondaryText: props.user?.email || '',
    },
  };

  const openNewTabWithUrl = (url: string): void => {
    window.open(url, '_blank');
  };

  // Notification Center Start
  const updateNotificationCount = (categories: INotificationCategory[]): void => {
    const currentTenantID = getTenantId();

    // Function to check if a notification item's tag matches the current tenant ID
    const hasMatchingTenantTag = (item: INotificationItem): boolean => {
      // TypeScript casting to access the tag property we added
      const extendedItem = item as INotificationItem & { tag?: string };
      const tag = extendedItem.tag?.toLowerCase().trim() || '';

      if (currentTenantID) {
        const normalizedTenantId = currentTenantID.toLowerCase();
        return (
          tag.includes(`tenantid=${normalizedTenantId}`) ||
          tag.includes(`tenantid:${normalizedTenantId}`) ||
          (!tag.includes('tenantid=') && !tag.includes('tenantid:'))
        );
      }
      // If no tenant ID, only include items with no tenant tag
      return tag === '' || (!tag.includes('tenantid=') && !tag.includes('tenantid:'));
    };

    const totalNotificationsCount = categories.reduce((acc, category) => {
      // Filter items based on both:
      // 1. Not being deleted
      // 2. Matching the tenant ID filter logic
      const filteredItems = category.items.filter(
        (item) =>
          item.notificationStatus !== NotificationStatus.DELETED &&
          hasMatchingTenantTag(item)
      );

      return acc + filteredItems.length;
    }, 0);

    setCountNotifications(totalNotificationsCount);
  };

  const getFromSessionStorage = (key: string): string => {
    return sessionStorage.getItem(key) ?? '';
  };

  const apiTokenFromSession = getFromSessionStorage(UATConstants.jwtTokenForApi);
  const coreApiTokenFromSession = getFromSessionStorage(UATConstants.jwtTokenForCoreApi);
  const graphTokenFromSession = getFromSessionStorage(UATConstants.jwtTokenForGraph);
  const userEmail = getFromSessionStorage(UATConstants.jwtUserAccountEmail);
  const notificationApiUrl = `${UATConstants.WebApiUrl}${UATConstants.ApiPaths.GetUserLevelNotifications}`;
  const dismissApiUrl = `${UATConstants.WebApiUrl}${UATConstants.ApiPaths.PostDisableUserLevelNotifications}`;

  // Notification Center End
  const _items: IContextualMenuItemProps[] = [];

  // Add Copilot (Beta) menu item if enabled
  if (isCopilotEnabled) {
    _items.push({
      icon: 'copilotbeta',
      iconProps: {
        iconName: 'copilotbeta',
      },
      className: copilotBetaClass,
      key: UATConstants.PlaceholdersAndTitles.CopilotBeta,
      ariaLabel: isCopilotDisabled
        ? 'Copilot is not yet available in this view.'
        : UATConstants.PlaceholdersAndTitles.CopilotBeta,
      text: UATConstants.PlaceholdersAndTitles.CopilotBeta,
      disabled: isCopilotDisabled,
      onClick: () => {
        copilotWindowOpenAction();
        registerEventForAppInsight('Copilot (Beta) Clicked');
      },
    });
  }

  // Add Analytics View menu item if enabled
  if (isAnalyticsEnabled) {
    _items.push({
      key: UATConstants.PlaceholdersAndTitles.AnalyticsViewIcon,
      onRenderIcon: (props: any) => <FluentAnalytics {...props} />,
      ariaLabel: UATConstants.PlaceholdersAndTitles.AnalyticsView,
      text: UATConstants.PlaceholdersAndTitles.AnalyticsView,
      onClick: () => {
        openAVPanel(true);
        registerEventForAppInsight('AnalyticsView Clicked');
      },
    });
  }

  // Add Request Access menu item if enabled
  if (isRequestAccessEnabled) {
    _items.push({
      key: UATConstants.PlaceholdersAndTitles.PeopleAdd,
      onRenderIcon: (props: any) => <PeopleAddFluent {...props} />,
      ariaLabel: UATConstants.PlaceholdersAndTitles.RequestAccess,
      text: UATConstants.PlaceholdersAndTitles.RequestAccess,
      onClick: () => {
        registerEventForAppInsight('RequestAccessLinkClicked');
        openNewTabWithUrl(UATConstants.Links.uatAccessLink);
      },
    });
  }

  // Add Settings menu item if enabled with enhanced tracking
  if (isSettingsEnabled) {
    _items.push({
      key: 'settings',
      icon: 'Settings',
      iconProps: { iconName: 'Settings' },
      ariaLabel: 'Settings',
      text: 'Settings',
      checked: Boolean(currentPanel === settingsPanelRef),
      onClick: handleSettingsClick,
    });
  }

  // Add Notification menu item if enabled
  if (isNotificationsEnabled) {
    _items.push({
      icon: 'Ringer',
      iconProps: {
        iconName: 'Ringer',
      },
      className: copilotBetaClass,
      key: 'notification',
      ariaLabel: 'Notification',
      text: 'Notification',
      onClick: () => {
        openNotifications();
        handleNotificationIconClick(notificationIconClicked);
      },
      checked: Boolean(currentPanel === notificationRef),
      onRenderIcon: (
        props: INotificationBadgeProps,
        defaultRender:
          | ((props?: IContextualMenuItemProps | undefined) => JSX.Element | null)
          | undefined
      ) => (
        <NotificationBadge
          {...props}
          numNewNotifications={notificationsCount}
          defaultRender={defaultRender}
          styles={{
            digitContainer: {
              backgroundColor: '#D13438',
            },
          }}
        />
      ),
    });
  }

  // Add Help menu item if enabled
  if (isHelpEnabled) {
    _items.push({
      key: UATConstants.PlaceholdersAndTitles.Help,
      icon: UATConstants.PlaceholdersAndTitles.Help,
      iconProps: { iconName: UATConstants.PlaceholdersAndTitles.Help },
      ariaLabel: UATConstants.PlaceholdersAndTitles.RequestHelpSupport2,
      text: UATConstants.PlaceholdersAndTitles.RequestHelpSupport2,
      checked: Boolean(currentPanel === helpPanelRef),
      onClick: () => {
        openPanel(helpPanelRef);
      },
    });
  }

  const formattedData = {
    primary: _items,
    overflow: [],
  };

  const [notificationIconClicked, setNotificationIconClicked] = useState<boolean>(false);

  const handleNotificationIconClick = (notificationIconClicked: boolean): void => {
    setNotificationIconClicked(!notificationIconClicked);
  };

  const getSearchPlaceholder = (): string => {
    const defaultPlaceholder = UATConstants.PlaceholdersAndTitles.SearchActions;
    if (!tenantUrlValue || tenantUrlValue === '' || !tenantConfig || !tenantConfig.nodeName) {
      return defaultPlaceholder;
    }
    if (urlTenantID === UATConstants.TenantIds.RB) {
      const assignedTeamDisplayNames = UATConstants.PlaceholdersAndTitles.RedButton;
      return `${defaultPlaceholder} in ${assignedTeamDisplayNames}`;
    }
    // Special handling for TECH_ROB tenant
    if (urlTenantID === UATConstants.TenantIds.TECH_ROB) {
      return `${defaultPlaceholder} in ${tenantConfig.searchNodeName}`;
    }
    // Special handling for COMPETE tenant
    if (urlTenantID === UATConstants.TenantIds.COMPETE) {
      return `${defaultPlaceholder} in ${tenantConfig.searchNodeName}`;
    }
    return `${defaultPlaceholder} in ${tenantConfig.nodeName}`;
  };

  const SharedEnhancedSearch = (
    <EnhancedSearch
      value={globalSearchFilterText}
      styles={{
        root: {
          width: isMobile ? '190px !important' : "minmax(240px, auto)",
        },
      }}
      placeholder={getSearchPlaceholder()}
      title={getSearchPlaceholder()}
      onSearch={(newVal) => {
        onSearchInputChanged(newVal);
        appInsights.trackEvent(
          {
            name: 'UAT-Dashboard-GlobalSearch',
            properties: {
              searchKeyword: newVal,
            },
          },
          GetUserAppInsightProperties()
        );
        if (currentTenantData?.nodeName?.[0] === TenantNames.MCAPSRoB) {
          getROBTemetetry(UATConstants.Telemetry.ROBGlobalSearch, UATConstants.Telemetry.ROBGlobalSearch, '', '');
        }
      }}
      onChange={(event: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
        setglobalSearchFilterText(newValue ? newValue : '');
      }}
      onClear={() => {
        onSearchInputChanged('');
        const globalSearch = document.getElementById('searchbox') as HTMLInputElement;
        if (globalSearch) {
          globalSearch.value = '';
          const inputEvent = new Event('input', { bubbles: true });
          globalSearch.dispatchEvent(inputEvent);
          const clearEvent = new CustomEvent('globalsearch-cleared', {
            bubbles: true,
            detail: { clearedValue: '' }
          });
          window.dispatchEvent(clearEvent);
        }
      }}
      isSearchInHeader={true}
      minHighlightThreshold={1}
    />
  );

  // Coherence Design System Functions End

  const Titlecomponent = (): JSX.Element => {
    return (
      <>
        <Link
          aria-label={UATConstants.PlaceholdersAndTitles.UnifiedActionTracker}
          rel='noopener'
          className={isParam ? 'disabledEvent' : 'clickable'}
          href={homeHref}
          underline={false}>
          <Stack horizontal verticalAlign='center'>
            <img src={UATlogo} alt='Action 360 Logo' id='uatLogo' width={48} height={48}></img>
            {/*commenting this one for future use */}
            {/* {UATConstants.PlaceholdersAndTitles.UnifiedActionTracker} */}
            {/* <span className={formerlyUatClass}>{UATConstants.PlaceholdersAndTitles.FormerlyUAT}</span> */}
            {UATConstants.PlaceholdersAndTitles.UnifiedActionTrackerBrandName}
          </Stack>
        </Link>
      </>
    );
  };

  //HTML Content Start
  return (
    <>
      <div className='coherence-nav-container' id="HeaderNavBarContainer" data-manageview="">
        {/* Coherence Design System */}
        <Header
          title={<Titlecomponent />}
          styles={{
            titleContainer: {
              marginLeft: '0',
            },
            root: {
              gridTemplateColumns: isMobile ? '0.5fr 200px 1fr !important' : '1fr 492px 1fr',
              '& > :nth-child(2)': {
                paddingInline: isMobile ? '6px' : '12px',
              },
            },
          }}>
          <Template slot='hamburger'>
            {(window.location.pathname === UATConstants.Routes.LandingPage ||
              window.location.pathname === UATConstants.Routes.DashboardSummary ||
              window.location.pathname.toLowerCase().replace(/\s/g, '') ===
              UATConstants.Routes.Home ||
              window.location.pathname === UATConstants.Routes.Overview ||
              window.location.pathname === UATConstants.Routes.Dashboard ||
              window.location.pathname === UATConstants.Routes.TenantOverviewHub ||
              window.location.pathname === UATConstants.Routes.ManageView) && (
                <NavigationHamburger
                  isNavCollapsed={props.isNavCollapsed}
                  toggleNavCollapsed={props.toggleNavCollapsed}
                />
              )}
          </Template>
          {isGlobalSearchEnabled && (
            <Template slot="search">
              {SharedEnhancedSearch}
            </Template>
          )}

          <Template slot="collapsible-triggers">
            <ResizeGroup
              styles={{ root: { background: 'transparent', height: '100%' } }}
              className='header-resize-group'
              data={formattedData}
              onReduceData={onReduceData}
              onGrowData={onGrowData}
              onRenderData={onRenderData}
            />
          </Template>

          <Template slot='static-triggers'>
            <TooltipHost content={'profile'} id={'profile'}>
              <HeaderButton
                onClick={() => openPanel(profileCenterRef)}
                checked={isEqual(currentPanel, profileCenterRef)}
                aria-describedby={'profile'}>
                <Persona
                  hidePersonaDetails={true}
                  size={PersonaSize.size28}
                  {...profileProps.persona}
                />
              </HeaderButton>
            </TooltipHost>
          </Template>
        </Header>
      </div>

      {/* Panels live outside the header. */}
      <>
        {renderAVPanel()}
        {redirectToDashboard && renderRedirectComponent()}
        {showTeachingBubbleForCopilot && (
          <TeachingBubble
            calloutProps={{
              directionalHint: DirectionalHint.bottomCenter,
            }}
            target={`#${buttonId}`}
            hasCloseButton={true}
            isWide={true}
            onDismiss={() =>
              toggleTeachingBubbleVisible(true, UATConstants.NotificationCategory.FeatureCopilot)
            }
            headline='UAT Copilot is now in beta!'>
            {
              legacyNotifications.find(
                (notification: any) =>
                  notification.category === UATConstants.NotificationCategory.FeatureCopilot
              )?.htmlMessage
            }
          </TeachingBubble>
        )}
        <ProfileCenter
          componentRef={profileCenterRef}
          isLightDismiss={true}
          onDismiss={() => closePanel()}
          {...profileProps}
        />

        <Suspense fallback={<></>}>
          <div>
            <NotificationPanel
              notificationIconClicked={notificationIconClicked}
              user={props.user ?? null}
              updateNotificationCount={updateNotificationCount}
              apiToken={apiTokenFromSession}
              coreApiToken={coreApiTokenFromSession}
              graphToken={graphTokenFromSession}
              userEmail={userEmail}
              notificationApiUrl={notificationApiUrl}
              dismissApiUrl={dismissApiUrl}
              handleNotificationIconClick={handleNotificationIconClick}
              ref={notificationRef}
            />
          </div>
        </Suspense>

        {/* Settings Panel */}
        <Panel
          componentRef={settingsPanelRef}
          onDismiss={closePanel}
          isOpen={Boolean(currentPanel === settingsPanelRef)}
          isLightDismiss={true}
          headerText='Settings'
          styles={{
            contentInner: {
              backgroundColor: '#fff',
            },
          }}
          // We need to explicitly define this function so that isLightDismiss will bubble
          // the outer click event to other header buttons
          // eslint-disable-next-line no-empty-function, @typescript-eslint/no-empty-function
          onOuterClick={(_e) => { }}>
          <LanguageDropdown userDetails={props.user} />
          <Toggle
            label="Server-driven forms (AOAI preview)"
            checked={serverDrivenForms}
            onChange={(_ev, checked) => {
              // The flag lives in the Redux store (in-memory). Read synchronously by the AOAI create-form
              // branches; the next form open picks up a change.
              setServerDrivenFormsFlag(!!checked);
              setServerDrivenForms(!!checked);
            }}
          />
          <Toggle
            label="Server-driven forms: recompute on every edit (escape hatch)"
            checked={legacyRecompute}
            onChange={(_ev, checked) => {
              // Escape hatch for the reactive-field gating: ON reverts the server-driven forms to
              // calling /view on every field edit. Read at render time by ServerDrivenForm — no
              // reload needed; the next form open picks it up.
              setLegacyRecomputeFlag(!!checked);
              setLegacyRecompute(!!checked);
            }}
          />
        </Panel>

        <HelpPanel
          ref={helpPanelRef}
          isOpen={Boolean(currentPanel === helpPanelRef)}
          onDismiss={closePanel}
        />

      </>
    </>
  );
};

// Enhanced memoized export with proper comparison function
export default React.memo(NavBar, (prevProps, nextProps) => {
  return (
    prevProps.isNavCollapsed === nextProps.isNavCollapsed &&
    prevProps.profileBase64 === nextProps.profileBase64 &&
    isEqual(prevProps.user, nextProps.user) &&
    isEqual(prevProps.account, nextProps.account)
  );
});
// Generated by GitHub Copilot | Updated on: 2025-12-16 | 3 lines modified | Added COMPETE tenant special handling for search placeholder text
// Generated by GitHub Copilot | Updated on: 2026-05-15 | 3 lines modified | Removed redundant initializeIcons() call to minimize dynamic style injection
// Generated by GitHub Copilot | Updated on: 2026-07-08 | 4 lines modified | Relabeled server-driven forms Settings toggle to mention Marketplace + reload on change so the nav injection re-runs
// Generated by GitHub Copilot | Updated on: 2026-05-25 | 1 line modified | Added explicit width/height to uatLogo image
// Generated by GitHub Copilot | Updated on: 2026-07-21 | 12 lines added | Added Settings toggle for the serverDrivenManageForms preview (Marketplace server-driven manage edit form)
